import React, { useEffect } from "react";
import { Row, Col, Button, Image } from "react-bootstrap";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import Title from "../../components/Title/Title";
import "./AboutAI.scss";

const aiIdeas = [
  {
    number: "1",
    title: "Process Automation",
    description: "Automate repetitive and time-consuming tasks, freeing up employees to focus on more strategic and creative initiatives.",
    image: require("../../assets/blog/blog-post-3-02.png")
  },
  {
    number: "2",
    title: "Supply Chain Optimization",
    description: "Analyze supply chain data to optimize inventory management, demand forecasting, and logistics, improving efficiency and reducing costs.",
    image: require("../../assets/blog/blog-post-3-03.png")
  },
  {
    number: "3",
    title: "Data Analytics & Insights",
    description: "Custom algorithms can analyze large volumes of data to extract valuable insights, helping businesses make data-driven decisions and identify trends and patterns.",
    image: require("../../assets/blog/blog-post-3-04.png")
  },
  {
    number: "4",
    title: "Quality Control & Defect Detection",
    description: "AI-powered image recognition systems can identify defects in products or monitor production processes for quality control purposes.",
    image: require("../../assets/blog/blog-post-3-05.png")
  },
  {
    number: "5",
    title: "Speech Recognition & Transcription",
    description: "AI can convert speech into text, making it easier to process and analyze customer calls, meetings, and interviews.",
    image: require("../../assets/blog/blog-post-3-06.png")
  },
  {
    number: "6",
    title: "Energy Management",
    description: "Custom algorithms can optimize energy consumption by analyzing usage patterns and making recommendations for energy-efficient practices.",
    image: require("../../assets/blog/blog-post-3-07.png")
  },
  {
    number: "7",
    title: "Content Creation & Curation",
    description: "AI can generate content, such as blog posts, social media updates, and product descriptions, saving time and resources for businesses.",
    image: require("../../assets/blog/blog-post-3-08.png")
  },
  {
    number: "8",
    title: "AI-Powered Customer Support",
    description: "Implementing AI chatbots or virtual assistants can provide instant and personalized customer support, improving response times and enhancing overall customer satisfaction.",
    image: require("../../assets/blog/blog-post-3-09.png")
  },
  {
    number: "9",
    title: "Intelligent Pricing Optimization",
    description: "Custom algorithms can analyze market conditions, competitor pricing, and customer behavior to optimize pricing strategies, maximizing revenue and profitability.",
    image: require("../../assets/blog/blog-post-3-10.png")
  },
  {
    number: "10",
    title: "Forecasting",
    description: "AI can analyze historical sales data, market trends, and external factors to provide accurate sales forecasts, assisting businesses in demand planning and inventory management.",
    image: require("../../assets/blog/blog-post-3-11.png")
  }
];

export default function AboutAI() {
  useEffect(() => {
    AOS.init();
    window.scrollTo(0, 0);
  }, []);

  return (
    <div id="ai_about" className="section section-bg py-5">
      <div className="container subpage-container">
        <Row data-aos="fade-down" data-aos-duration="500">
          <Col xs={12} className="text-center">
            <Title
              regular_text="Revolutionize Your Business with our"
              highlighted_text="Custom Solutions & AI"
            />
            <div className="ratio ratio-16x9 my-4 video-wrapper shadow-sm">
              <iframe
                src="https://www.youtube.com/embed/4roU53Eay8A"
                title="AI & Custom Solutions Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </Col>
        </Row>

        <Row data-aos="fade-up" data-aos-duration="500" className="my-4">
          <Col xs={12}>
            <p className="lead-text">
              Transform your business operations with custom software tailored to your
              unique needs. We combine AI-driven solutions and proven traditional methods
              to streamline workflows, reduce costs, and boost efficiency. Our team crafts
              intelligent systems that adapt to your processes, turning complexity into
              clarity. From automation to analytics, we provide tools that empower smarter
              decision-making and measurable growth.
            </p>
            <p className="lead-text">
              Embark on your journey to optimize operations
              and unlock your business’s full potential with a complimentary consultation
              tailored to your unique needs and style. We'll recommend a
              customized plan and provide you with an initial estimate of the
              project's overall cost.
            </p>
          </Col>
        </Row>

        {/* 10 Ideas Section */}
        <div className="ideas-section my-5">
          <Row data-aos="fade-up" data-aos-duration="500">
            <Col xs={12} className="text-center mb-5">
              <Title
                regular_text="10 Ideas on how"
                highlighted_text="Custom Software and AI can Assist your Business"
              />
            </Col>
          </Row>

          {aiIdeas.map((idea, index) => {
            const isEven = index % 2 === 0;
            return (
              <Row
                key={idea.number}
                className="align-items-center mb-5 step-row"
                data-aos="fade-up"
                data-aos-duration="500"
              >
                {isEven ? (
                  <>
                    <Col xs={12} md={6} className="order-2 order-md-1 text-md-end">
                      <div className="step-content">
                        <h3>{idea.number}. {idea.title}</h3>
                        <p>{idea.description}</p>
                      </div>
                    </Col>
                    <Col xs={12} md={6} className="order-1 order-md-2 text-center text-md-start mb-3 mb-md-0">
                      <Image
                        src={idea.image}
                        className="step-image rounded-circle shadow-sm"
                        alt={idea.title}
                      />
                    </Col>
                  </>
                ) : (
                  <>
                    <Col xs={12} md={6} className="text-center text-md-end mb-3 mb-md-0">
                      <Image
                        src={idea.image}
                        className="step-image rounded-circle shadow-sm"
                        alt={idea.title}
                      />
                    </Col>
                    <Col xs={12} md={6} className="text-md-start">
                      <div className="step-content">
                        <h3>{idea.number}. {idea.title}</h3>
                        <p>{idea.description}</p>
                      </div>
                    </Col>
                  </>
                )}
              </Row>
            );
          })}
        </div>

        {/* Pricing / Costs Section */}
        <div id="ai_pricing" className="costs-section my-5" data-aos="fade-up" data-aos-duration="500">
          <Row>
            <Col xs={12} className="text-center mb-4">
              <Title regular_text="About" highlighted_text="Costs" />
            </Col>
            <Col xs={12}>
              <p className="lead-text">
                The costs associated building and adopting custom software can vary widely depending on several
                factors, including the scope and complexity of the project, the industry
                or domain it is being applied to, the available data, and the desired level
                of sophistication. Some organizations start with smaller-scale pilot
                projects to test the feasibility and benefits before committing
                to larger-scale implementations. We encourage you to reach out to us
                without hesitation, and once we have a clear understanding of your business,
                we can develop a personalized plan and offer you a customized quote.
              </p>
              <div className="text-center my-4">
                <Button as={Link} to="/home/contact" size="lg" variant="primary" className="btn-main">
                  Contact Us
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}
